import create from 'zustand';
import { GetState, SetState, StateSelector, EqualityChecker } from 'types/zustand';
import { useCallback, useMemo } from 'react';
import { IActionBarClickEvent, IActionBarItemData } from 'components/ActionsBar/ActionsBarItem';
import {
  getActionsForAnyEntity,
  verifyPermissionAndGetFolderViewData,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/FolderService';
import {
  FolderChangedMessage,
  FolderViewData,
  PortalActionsBarVisibility,
  SilverlightPortalPage,
  ViewPageData,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder';
import { getActionBarItemsFromHeaders } from 'components/ActionsBar/ActionBarUtils';
import { IActionLinksPair } from 'components/ActionLinks/ActionLinkModels';
import {
  ActionHeader,
  BaseActionType,
  EntityActionType,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Actions';
import { PageLinkPathProps } from 'domains/portal/Header/NavigationPanel/PageLinks/PageLinks';
import { FolderPathElement } from 'domains/portal/Header/NavigationPanel/Breadcrumbs/Breadcrumbs';
import { produce } from 'immer';
import { addClientEventListener, ClientMessage } from 'utils/ClientEventUtils';
import {
  doesAllowSingleActions,
  filterActions,
  getActionLinksForActions,
  isNavigateAction,
  performAction,
} from 'utils/ActionUtils';
import { getCookieDisplayType } from 'utils/ApiUtils';
import { decodeHTML } from 'utils/StringUtils';
import { getActionHeadersForAnyEntity } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions/Common/CommonActionService';
import { getLocationPartsFromUrl } from 'components/Router/RouterUtils';
import { getIsMobile } from 'config/PortalConfiguration';
import { useFolderActionsStore } from './ActionsStore';
import { getPortalStoreConnector } from './PortalStoreConnector';

export interface IExampleSelector<U> {
  (selector: StateSelector<IFolderStore, U>, equalityFn?: EqualityChecker<U>): U;
}

/*
 * This might not be the right place for this, as it's in _domains_, but it's possible that we'll have
 * to have things in _components_ that reference the current folder store and we'll need to move it to the root.
 *
 * For now it's managed by the PortalFrame so I'm sticking it in the /portal folder
 */

const folderStoreCleanupTimeouts = new Map<string, number>();
const subscriptionTimeouts = new Map<string, number>();
const folderStoreClearInterval = 600000; // 10 mins. camel case because is it configurable?
const unsubscribeInterval = 30000; // if the user navigates back and forth within 30 seconds, don't clear all subscriptions in that time.

function cancelUnCacheStore(id: string) {
  const timeoutId = folderStoreCleanupTimeouts.get(id);
  if (timeoutId) {
    window.clearTimeout(timeoutId);
  }

  const unsubscribeTimeoutId = subscriptionTimeouts.get(id);
  if (unsubscribeTimeoutId) {
    window.clearTimeout(unsubscribeTimeoutId);
  }
}

//#region store creation

/**
 * Initial state for a particular folder, via factory so we can't accidentally modify this. (immutability)
 */
function getDefaultStateForFolder(folderId: string) {
  return {
    actionBarVisible: true,
    pageContextId: '',
    folderId,
    folderViewData: (undefined as unknown) as FolderViewData | undefined,
    /** Actions for page header, does not include navigation actions */
    headerActions: {
      originalActionsById: new Map(),
      actionLinks: {},
    } as IActionLinksPair,
    /** Actions for folder tree, includes navigation actions */
    folderTreeActions: {
      originalActionsById: new Map(),
      actionLinks: {},
    } as IActionLinksPair,
    activeActionGroupName: '',
    activePage: '' as string | undefined,
    /** This will be the ConfigurationStorageId */
    activePageId: '' as string | undefined,
    pageActions: {} as {
      [pageViewId: string]: IActionLinksPair;
    },
  };
}

/** Using returnType avoids defining separate interface by hand */
type FolderState = ReturnType<typeof getDefaultStateForFolder>;

const defaultFoldersState = {
  folders: new Map<string, FolderState>(),
  currentFolderId: '',
  /** Used to track which folder's actions menu should be open in the Folder Tree, if any */
  activeMenuFolderId: '',
  /**
   * Used to make distinct an activeFolder's actions, in the scenario it is duplicated, e.g. virtual root, and nested
   * so we have no duplicate action menus from reading a from a single folderId.
   */
  activeMenuParentFolderId: '',
};

type FoldersState = typeof defaultFoldersState;

type FolderStoreActions = {
  folderStoreActions: ReturnType<typeof createFolderStoreActions>;
};

// seems like this is maybe about folder navigation, not page data?
type IFolderStore = FoldersState & FolderStoreActions;

// export const createFolderStore = (folderId: string) => {
export const useFolderStore = create<IFolderStore>((set, get) => ({
  ...defaultFoldersState,
  folderStoreActions: createFolderStoreActions(get, set),
}));

// should subscribe to one of these or both to update folderViewData:
// "FolderEntitiesChangedMessage"
// "FolderChangedMessage"

/** Describe what's being seeing in the client for these. */

addClientEventListener(
  ClientMessage.FolderChanged,
  'folder-store',
  ({ FolderId, FolderViewData, IsProcessed }: FolderChangedMessage) => {
    if (!FolderId) {
      return;
    }
    const store = useFolderStore.getState();
    useFolderActionsStore.getState().treeShim.refreshNode(FolderId);

    if (IsProcessed) {
      // we already processed the payload, because an action handler in this browser tab is the source of this updated FolderViewData
      return;
    }

    const { updateFolderViewData, fetchAndUpdateFolderViewData } = store.folderStoreActions;
    // TODO Efficiency: per Khu - can we avoid FolderViewData as part of this update event? (would be server side change)
    if (FolderViewData) {
      // just consume the update
      updateFolderViewData(FolderViewData, getFolderState(store, FolderId).activePage);
    } else if (useFolderStore.getState().folders.get(FolderId)) {
      // fetch and update if we have a stale version, otherwise, we'll fetch it when we navigate there.
      fetchAndUpdateFolderViewData(FolderId, getFolderState(store, FolderId).activePage);
    }
  }
);

function createFolderStoreActions(get: GetState<IFolderStore>, set: SetState<IFolderStore>) {
  return {
    /**
     * Loads, initializes and sets folder as current.
     * @param folderId to initialize
     * @param navigate `false` to initialize without setting currentFolderId. default `true`
     */
    goToFolder: async (folderId: string, pageName?: string, navigate = true) => {
      const { folderStoreActions, currentFolderId } = get();
      let folder = getOrCreateFolderStateObj(get(), folderId);

      // schedule un-cache of previous folder's data
      folderStoreActions.scheduleUnCacheAndUnsubscribe(currentFolderId);

      set(
        produce<IFolderStore>(state => {
          folder = { ...folder };
          state.folders.set(folderId, folder);
          state.currentFolderId = navigate ? folderId : currentFolderId;
        })
      );

      const { fetchAndUpdateFolderViewData, subscribeToEvents } = folderStoreActions;

      const folderViewData = await fetchAndUpdateFolderViewData(folderId, pageName);
      await subscribeToEvents();

      // cancel any timers to un-cache data for the current folder.
      cancelUnCacheStore(folderId);
      return folderViewData;
    },

    setActivePage: async (pageName?: string) => {
      if (!pageName) {
        return; // do nothing, eh? or reset the default, perhaps
      }
      const {
        currentFolderId,
        folderStoreActions: { loadPageActions },
      } = get();

      const folderViewData = getFolderState(get(), currentFolderId).folderViewData;
      if (!folderViewData) {
        // this should be impossible, unless you broke something else or are misusing this.
        console.warn(`Somehow selected page "${pageName}" without FolderViewData loaded for ${currentFolderId}.`);
        return;
      }

      // make sure we update the activePageId, too.
      const { activePage, activePageId } = findActivePageOrDefault(folderViewData, true, pageName);

      if (activePage !== pageName) {
        // this should also be impossible unless someone is breaking things.
        console.warn('An invalid pageName was passed!', pageName);
      }

      // set the page name and ID immediately, in case something else is comparing current page and deciding to load or not.
      set(
        produce<IFolderStore>(state => {
          state.folders.get(currentFolderId)!.activePage = pageName;
          state.folders.get(currentFolderId)!.activePageId = activePageId;
        })
      );

      // update page actions here as well.
      const actions = await loadPageActions(pageName);
      set(
        produce<IFolderStore>(state => {
          state.folders.get(currentFolderId)!.pageActions = {
            ...state.folders.get(currentFolderId)!.pageActions,
            ...actions,
          };
        })
      );
    },

    loadPageActions: async (activePage?: string, folderId?: string, folderViewData?: FolderViewData) => {
      const state = get();
      folderId = folderId || state.currentFolderId;
      folderViewData = folderViewData || folderViewDataSelector(state);
      activePage = activePage || getActivePageName(state);

      if (!folderViewData || !activePage) {
        // this isn't loaded yet, and we need to wait. It's probably being handled
        return;
      }

      const pageData = getPageDataByName(folderViewData.Pages, activePage);

      let pageActions: ActionHeader[] = [];

      if (folderViewData.Pages.length < 1) {
        // no page to display... theoretically possible?
        getPortalStoreConnector().showEmptyPage(true);
        return;
      } else {
        if (pageData && (pageData as SilverlightPortalPage).ViewPageID) {
          const { handleAction } = state.folderStoreActions;
          const viewPageId = (pageData as SilverlightPortalPage).ViewPageID;
          const actions = await getActionHeadersForAnyEntity({
            actionTypes: [EntityActionType.Silverlight, EntityActionType.WebForm],
            typeName: 'DecisionsFramework.ServiceLayer.Services.Folder.PageData',
            entityID: viewPageId,
          });

          if (actions?.length) {
            pageActions = actions;
            // these _should_ be cataloged ty viewPageId, almost certainly, but all the page selection logic is set to
            // work off page _name_ currently, and is it likely users will configure multiple pages with the same name?
            return { [activePage]: getActionLinksForActions(pageActions, handleAction) };
          }
        }
      }
    },

    toggleActionsVisible: () => {
      const { currentFolderId } = get();

      if (!currentFolderId) {
        return;
      }

      set(
        produce<IFolderStore>(state => {
          const folder = getCurrentFolderState(state);
          state.folders.get(currentFolderId)!.actionBarVisible = !folder.actionBarVisible;
        })
      );

      // can we just replace this with the screen size store?
      getPortalStoreConnector().onPageAreaSizeChange();
    },

    handleAction: (_action?: BaseActionType | ActionHeader) => {
      // letting default handler execute action, but we're closing overlay, etc.
      const { currentFolderId } = get();
      set(
        produce<IFolderStore>(state => {
          state.folders.get(currentFolderId)!.activeActionGroupName = '';
          state.activeMenuFolderId = '';
          state.activeMenuParentFolderId = '';
        })
      );
    },

    fetchAndUpdateFolderViewData: async (folderId: string, pageName?: string) => {
      const { folderStoreActions } = get();
      const { updateFolderViewData } = folderStoreActions;
      const actionTypes: EntityActionType[] = []; // actionTypes are now read out of the UserContext on the server.
      const folderViewData = await verifyPermissionAndGetFolderViewData({ folderID: folderId, actionTypes });
      await updateFolderViewData(folderViewData, pageName);
      return folderViewData;
    },

    renameFolderNameInFolderViewData: async (folderId: string, newFolderName: string) => {
      const state = get();
      const { folderStoreActions } = state;
      const { updateFolderViewData } = folderStoreActions;
      let folderViewData = getFolderState(state, folderId).folderViewData;
      if (folderViewData) {
        folderViewData.FolderName = newFolderName;
        await updateFolderViewData(folderViewData);
      }
    },

    updateFolderViewData: async (folderViewData: FolderViewData, pageName?: string) => {
      // TODO we shouldn't refetch on navigation if update events are working
      // make sure old caches are replaced/ removed
      const state = get();
      const { folderStoreActions } = state;
      const { handleAction, loadPageActions, scheduleUnCacheIfNotCurrentFolder } = folderStoreActions;
      if (!folderViewData) {
        return;
      }

      // if it's an alias, save FVD for the alias, otherwise the real folder:
      const folderId = folderViewData.AliasFolderId || folderViewData.FolderID;
      const isCurrentFolder = getCurrentFolderId(state) === folderId;

      // filter pages by display type:
      folderViewData.Pages = filterPagesByDisplayType(folderViewData.Pages);

      const { folderTreeActions, headerActions } = processFolderActions(
        folderId,
        folderViewData.ActionHeaders,
        handleAction
      );

      const prevActivePageId = getFolderState(state, folderId).activePageId;
      const { activePage, activePageId, pageWasRemoved, pageWasRenamed } = findActivePageOrDefault(
        folderViewData,
        isCurrentFolder,
        pageName,
        prevActivePageId
      );

      if (pageWasRemoved) {
        // Briefly showed a warning dialog at for deleted page scenario, but there are too many caveats to that,
        // such as "don't warn the user doing the deleting," and "hidden by page visibility rules",
        // which looks identical to deleted page. Better too few warning dialogs than too many.

        // We also needed to _navigate_ here, pre "Nav Squad," but now navigation will happen in response to the URL change:
        getPortalStoreConnector().updatePageName(activePage);
      } else if (pageWasRenamed) {
        // Replace pageName in URL as well. Do this before state updates so PageLinks read updated active PageName from URL correctly.
        // If that wasn't necessary, we could do this last, and ideally avoid page content refresh in this scenario.
        getPortalStoreConnector().updatePageName(activePage);
      }

      // TODO load pageActions from PortalLoadModel initially, too...
      // active page assigned and evaluated by name, because a different dashboard could be the same viewPageId, but with different filters, etc.
      // We only want to fetch page actions for the folder currently shown in the Portal. During navigation, currentFolderId must always be set first.
      let pageActions = isCurrentFolder ? await loadPageActions(activePage, folderId, folderViewData) : {};

      scheduleUnCacheIfNotCurrentFolder(folderId);

      set(
        produce<IFolderStore>(state => {
          const folder = getFolderState(state, folderId);
          folder.actionBarVisible = folderViewData.DefaultActionsVisibility === PortalActionsBarVisibility.Show;
          const updated: FolderState = {
            ...folder,
            folderViewData,
            activePage,
            activePageId,
            headerActions,
            folderTreeActions,
            pageActions: { ...folder.pageActions, ...pageActions },
          };
          state.folders.set(folderId, updated);
        })
      );
    },

    getFolderViewData: async (folderId = getCurrentFolderId(get())) => {
      // per legacy Decisions.LocalServices.CachingService, if this already exists, just return it...
      // not sure how/when this would ever get updated if it needed to be though... folder change event subscriptions?
      const state = get();
      const { scheduleUnCacheIfNotCurrentFolder, fetchAndUpdateFolderViewData } = state.folderStoreActions;
      const currentFolderId = getCurrentFolderId(state);
      let folderViewData = getFolderState(state, folderId).folderViewData;

      scheduleUnCacheIfNotCurrentFolder(folderId, currentFolderId);

      if (folderViewData) {
        return folderViewData;
      }

      // use fetchAndUpdateFolderViewData to fetch, so stored data is always processed the same way.
      folderViewData = await fetchAndUpdateFolderViewData(folderId);

      if (!folderViewData) {
        return;
      }

      set(
        produce<IFolderStore>(state => {
          const folderState = getOrCreateFolderStateObj(state, folderId);
          state.folders.set(folderId, { ...folderState, folderViewData });
        })
      );

      return folderViewData;
    },

    /**
     * This is for use in legacy, non-promise code, that does not expect the callback to fire if `folderId` is falsy.
     *
     * If you are writing something new, just use `getFolderViewData` directly.
     *
     * This emulates behavior of old `Decisions.LocalServices.CachingService.GetFolderViewData`, which
     * 1. Only fired its callback when the folderId argument was truthy.
     * 1. Was synchronous: could also be used to return directly, no callback, no promise.
     * 1. Dealt in explicit `null` for not found values.
     *
     * _(This made the original implementation confusing, and its use a bit error prone. Should have just been two methods ;))_
     *
     * @param folderId to fetch
     * @param callback to trigger if Folder is found,
     * @return FolderViewData or null
     * @deprecated this is just for legacy support, not new stuff.
     */
    getFolderViewDataForLegacy: (folderId = '', callback?: (fvd: FolderViewData | null) => void) => {
      if (!folderId) {
        return null;
      }

      const folderViewData = getFolderState(get(), folderId).folderViewData;

      get()
        .folderStoreActions.getFolderViewData(folderId)
        .then(folderViewData => {
          callback?.(folderViewData || null);
        })
        .catch(_ => callback?.(null));

      return folderViewData || null;
    },

    /**
     * Check for pre-loaded actions in FolderViewData, and use that. Otherwise, just fetch the actions themselves to avoid unnecessary overhead
     */
    ensureFolderActionsLoaded: async (folderId: string) => {
      const state = get();
      if (getFolderState(state, folderId).folderViewData) {
        return;
      }

      const folder = getFolderState(state, folderId);

      // either this isn't the right method, or we need something different here, because the returned action headers are not the same
      // added [DT-035628] to make this useable.
      const actions = await getActionsForAnyEntity({
        // per DisplayTypeHelper.ConvertDisplayTypeToEntityActionType, for HTML portal. Need to beef these things in future.
        actionTypes: [EntityActionType.Mvc],
        entityID: folderId,
        typeName: 'DecisionsFramework.ServiceLayer.Services.Folder.Folder',
      });

      const { folderTreeActions } = processFolderActions(
        folderId,
        actions,
        state.folderStoreActions.handleAction,
        true
      );

      set(
        produce<IFolderStore>(state => {
          const updated: FolderState = {
            ...folder,
            folderTreeActions,
          };
          state.folders.set(folderId, updated);
        })
      );
    },

    /**
     * Updates FolderViewData, only if it already exists in the store.
     *
     * NOOP if it doesn't exist, or `folderId` is falsy.
     *
     * @param folderId folder things
     * @returns updated `FolderViewData`
     */
    refreshFolderIfExist: async (folderId: string) => {
      if (!folderId) {
        return;
      }

      const state = get();

      if (state.folders.has(folderId)) {
        return state.folderStoreActions.fetchAndUpdateFolderViewData(folderId);
      }
    },
    deleteFolderFromStorage: (folderID: string) => {
      set(
        produce<IFolderStore>(state => {
          state.folders.delete(folderID);
        })
      );
    },
    clearFolderStorage: () => {
      set(
        produce<IFolderStore>(state => {
          state.folders.clear();
        })
      );
    },

    subscribeToEvents: async () => {
      // TODO
    },

    unSubscribeToEvents: (_folderId: string) => {
      // TODO
    },

    /**
     * If we are concerned about memory growth from all the folder view data accumulating in a browser session over time...
     * @param folderId of store to un-cache and clear subscriptions for
     * @param immediate `true` to immediately unsubscribe and un-cache, otherwise will be on timeouts (10 mins)
     */
    scheduleUnCacheAndUnsubscribe: (folderId: string, immediate = false) => {
      const { unSubscribeToEvents } = getFolderStoreActions(get());
      if (immediate) {
        unSubscribeToEvents(folderId);
        return set(
          produce<IFolderStore>(state => {
            state.folders.delete(folderId);
          })
        );
      }

      // clear any previously set timeouts, restarting the clock for that ID:
      window.clearTimeout(folderStoreCleanupTimeouts.get(folderId));

      // TODO should these clear in same interval as pageContextIds get cleaned up?
      const timeoutId = window.setTimeout(() => {
        set(
          produce<IFolderStore>(state => {
            state.folders.delete(folderId);
          })
        );
        folderStoreCleanupTimeouts.delete(folderId);
      }, folderStoreClearInterval);

      folderStoreCleanupTimeouts.set(folderId, timeoutId);

      // also schedule unsubscribes:
      const unsubscribeTimeoutId = window.setTimeout(() => {
        unSubscribeToEvents(folderId);
      }, unsubscribeInterval);

      subscriptionTimeouts.set(folderId, unsubscribeTimeoutId);
    },

    scheduleUnCacheIfNotCurrentFolder: (folderId: string, currentFolderId = getCurrentFolderId(get())) => {
      if (folderId !== currentFolderId) {
        // we're asking for it, but we aren't navigated there, so we don't want to keep it forever
        get().folderStoreActions.scheduleUnCacheAndUnsubscribe(folderId);
      }
    },

    handlePageAction: (e: IActionBarClickEvent) => {
      const folderActions = getFolderActionsForHeader(get());
      const { currentFolderId: folderId } = get();
      if (e.item.isGroup) {
        set(
          produce<IFolderStore>(state => {
            state.folders.get(folderId)!.activeActionGroupName = e.item.label;
          })
        );
        // show action links for this thing :D
      }
      const action = folderActions.originalActionsById.get(e.item.id);
      // openAction(e.element, actions[0].Nm, model.entityId, actions[0].Id, null, null, null, null, model.entityTypeName);
      if (!action) {
        return;
      }
      performAction(
        e.element,
        action.Nm,
        folderId,
        action?.Id,
        null,
        undefined,
        undefined,
        undefined
        // model.entityTypeName // what would entity type name be for a folder?
      );
    },

    closePageAction: () => {
      const { currentFolderId: folderId } = get();
      set(
        produce<IFolderStore>(state => {
          state.folders.get(folderId)!.activeActionGroupName = '';
          state.activeMenuFolderId = '';
          state.activeMenuParentFolderId = '';
        })
      );
    },

    setActiveTreeMenuId: (activeMenuFolderId = '', activeMenuParentFolderId = '') => {
      // get().actions.closePageAction() // should happen first anyway
      set({ activeMenuFolderId, activeMenuParentFolderId });
    },
  };
}

//#endregion store creation

//#region selector functions

// selector functions could just as easily be inline, but having them pre-defined can DRY up
// reuse, and make them easier to test if they had a complex calculation.
// They also shield components from the effects of store-object refactors.

export function getCurrentFolderId(state: IFolderStore) {
  return state.currentFolderId;
}

export function getActionBarVisible(state: IFolderStore) {
  return getCurrentFolderState(state).actionBarVisible;
}

export function folderViewDataSelector(state: IFolderStore) {
  // using "selector" name here to avoid conflict on web.core side, since TS generation is imperfect.
  return getCurrentFolderState(state).folderViewData;
}

export function getCurrentPageId(state: IFolderStore) {
  return getCurrentFolderState(state).activePageId;
}

export function getFolderActionsForHeader(state: IFolderStore) {
  return getCurrentFolderState(state).headerActions;
}

export function getFolderStoreActions(state: IFolderStore) {
  return state.folderStoreActions;
}

export function getCanPinActions(state: IFolderStore) {
  return getCurrentFolderState(state).folderViewData?.AllowUserPinActions;
}

export function getActionsBarAvailability(state: IFolderStore) {
  return getCurrentFolderState(state).folderViewData?.DefaultActionsVisibility !== PortalActionsBarVisibility.Hide;
}

export function getFolderName(state: IFolderStore) {
  return getCurrentFolderState(state).folderViewData?.FolderName || '';
}

export function getDefaultPage(state: IFolderStore) {
  return getCurrentFolderState(state).folderViewData?.DefaultPageName || '';
}

export function getPages(state: IFolderStore) {
  return getCurrentFolderState(state).folderViewData?.Pages || [];
}

export function getActivePageName(state: IFolderStore) {
  return getCurrentFolderState(state).activePage || getCurrentFolderState(state).folderViewData?.DefaultPageName || '';
}

export function getActiveActionGroupName(state: IFolderStore) {
  return getCurrentFolderState(state).activeActionGroupName;
}

export function getVisibleActionLinks(state: IFolderStore) {
  if (getCurrentFolderState(state).activeActionGroupName === 'All') {
    return getCurrentFolderState(state).headerActions.actionLinks;
  }
  return getCurrentFolderState(state).headerActions.actionLinks.nested?.get(
    getCurrentFolderState(state).activeActionGroupName
  );
}

export function getVisibleActionsForFolderTree(state: IFolderStore) {
  if (!state.activeMenuFolderId) {
    return;
  }
  // if the folder is not loaded, yet, then we need to wait for it to load:
  return state.folders.get(state.activeMenuFolderId)?.folderTreeActions;
}

export function getVisiblePageActions(state: IFolderStore) {
  if (getCurrentFolderState(state).activeActionGroupName === 'All') {
    const pageName = getActivePageName(state);
    const currentFolderState = getCurrentFolderState(state);
    const pageActions = currentFolderState.pageActions;
    const currentPageActions = pageActions[pageName];
    return currentPageActions?.actionLinks;
  }
}

export function getActionsById(state: IFolderStore) {
  return getCurrentFolderState(state).headerActions.originalActionsById;
}

export function getFolderState(state: IFolderStore, folderId: string) {
  return state.folders.get(folderId) ?? getDefaultStateForFolder(folderId);
}

export function getCurrentFolderState(state: IFolderStore) {
  return getFolderState(state, state.currentFolderId);
}
export function getCurrentFolderDefaultPageName(state: IFolderStore) {
  return getCurrentFolderState(state)?.folderViewData?.DefaultPageName;
}

//#endregion

//#region hooks
// hooks help DRY up boilerplate around getting certain references, e.g.

export function useGetPageActions(): IActionBarItemData[] {
  const actions = useFolderStore(getFolderActionsForHeader);
  return useMemo(() => getActionBarItemsFromHeaders(actions), [actions]);
}

export function useGetPageLinks(): PageLinkPathProps[] {
  let pages = useFolderStore(state => folderViewDataSelector(state)?.Pages || []);
  const defaultPage = useFolderStore(getDefaultPage);
  const current = getLocationPartsFromUrl().pageName || defaultPage; // ideally, read from URL, also because of funny timing issue DT-036432
  const folderId = useFolderStore(getCurrentFolderId);
  // TODO filter based on display type
  return useMemo(() => {
    const sorted = [...pages].sort(comparePages);
    return sorted.map(page => ({
      name: page.ViewPageName,
      isCurrent: page.ViewPageName === current,
      viewPageId: (page as SilverlightPortalPage).ViewPageID ? (page as SilverlightPortalPage).ViewPageID : '',
      folderId,
    }));
  }, [pages, current, folderId]);
}

export function useGetFolderPath(): FolderPathElement[] {
  const folderView = useFolderStore(folderViewDataSelector);

  return useMemo(() => {
    const result: FolderPathElement[] = [];
    if (folderView) {
      for (let i = folderView.Parents.length - 1; i >= 0; i--) {
        result.push({
          folderName: folderView.Parents[i].FolderDisplayName,
          folderId: folderView.Parents[i].FolderId,
          isCollapsed: false,
        });
      }
      result.push({
        folderName: folderView.Alias != null ? folderView.Alias : folderView.FolderName,
        folderId: folderView.FolderID,
        isCollapsed: false,
      });
    }

    return result;
  }, [folderView]);
}

/** Compare some prop folderId to state, emits on change of result */
export function useIsShowingFolderTreeActions(folderId: string, parentId = '') {
  return useFolderStore(
    useCallback(state => state.activeMenuFolderId === folderId && state.activeMenuParentFolderId === parentId, [
      folderId,
      parentId,
    ])
  );
}

/**
 * Hook to show actions in the folder tree
 * @param folderId to show
 * @returns actions or undefined
 */
export function useFolderTreeMenuActions(folderId: string, parentId = '') {
  return useFolderStore(
    useCallback(
      state =>
        state.activeMenuFolderId === folderId && state.activeMenuParentFolderId === parentId
          ? getVisibleActionsForFolderTree(state)
          : undefined,
      [folderId, parentId]
    )
  );
}

//#endregion hooks

function getOrCreateFolderStateObj(state: IFolderStore, folderId: string) {
  return state.folders.get(folderId) || getDefaultStateForFolder(folderId);
}

export function comparePages(a: ViewPageData, b: ViewPageData) {
  const order = a.Order - b.Order;

  return order === 0 ? (a.ViewPageName < b.ViewPageName ? -1 : 1) : order;
}

function filterPagesByDisplayType(pages: ViewPageData[]) {
  const displayType = getCookieDisplayType();
  return pages.filter(page => page.DisplayType.includes(displayType));
}

function getPageConfigId(page: ViewPageData) {
  return (page as SilverlightPortalPage).ConfigurationStorageID || '';
}

function findActivePageOrDefault(
  folderViewData: FolderViewData,
  isCurrentFolder: boolean,
  activePageName = '',
  activePageId = ''
): {
  activePage: string;
  activePageId: string;
  pageWasRenamed?: boolean;
  pageWasRemoved?: boolean;
} {
  // if page name is not provided it's always going to go back to the default.
  // Trying to be clever about previously active page _by name_ just caused conflicts with
  // behaviors like coming from breadcrumbs which expected to navigate to default,
  // even when default is changing.
  if (!activePageName) {
    // a page was not provided, just select teh default:
    return findDefaultPage(folderViewData);
  }

  const pageByName = folderViewData.Pages.find(page => page.ViewPageName === activePageName);
  if (pageByName) {
    return {
      activePage: pageByName.ViewPageName,
      activePageId: getPageConfigId(pageByName),
    };
  }

  if (isCurrentFolder) {
    // for current folder only, try to resolve active page by ID:
    const pageById = folderViewData.Pages.find(page => getPageConfigId(page) === activePageId);
    if (pageById) {
      return {
        activePage: pageById.ViewPageName,
        activePageId,
        pageWasRenamed: true,
      };
    }
  }

  // we didn't find the intended page at all, so we probably need to navigate to the default:
  return {
    ...findDefaultPage(folderViewData),
    pageWasRemoved: isCurrentFolder,
  };
}

function findDefaultPage(folderViewData: FolderViewData) {
  const defaultPage = folderViewData.Pages.find(page => page.ViewPageName === folderViewData.DefaultPageName);
  if (!defaultPage) {
    // It's pretty likely the default page will not be configured to be visible on mobile, as things currently stand, reduce log level.
    const log = getIsMobile() ? console.log : console.warn;
    log(
      `Current or default page "${folderViewData.DefaultPageName}" is missing for folder. Could be a bad folder configuration, or simply page visibility rules.`,
      folderViewData.FolderName,
      'ID:',
      folderViewData.FolderID
    );
  }
  return {
    activePage: defaultPage?.ViewPageName ?? folderViewData.Pages[0]?.ViewPageName,
    activePageId: defaultPage ? getPageConfigId(defaultPage) : '',
  };
}

function getPageDataByName(pages: ViewPageData[], pageName: string) {
  // curious if decodeHTML is really necessary in ViewPageName...
  return (pages ?? []).find(page => decodeHTML(page.ViewPageName) === decodeHTML(pageName));
}

function filterFolderActions(actions: ActionHeader[], folderId: string) {
  return actions.filter(item => {
    if (!doesAllowSingleActions(item)) {
      // don't add group actions
      return false;
    }

    if (isNavigateAction(item)) {
      if (item.Nm.toString().indexOf('Preview') === 0) {
        // don't add preview actions
        return false;
      }

      var itemFolderId = item.Ni.toString().split('|')[0];
      // var itemPageName = item.Ni.toString().split('|')[1]; // unused in legacy... wat
      var itemNavigationType = item.Ni.toString().split('|')[2];

      if (itemNavigationType !== '1') {
        // if it's the same folder we're on, don't add it.
        return itemFolderId !== folderId;
      }
    }

    return true;
  });
}

/** Process Actions into ActionLinkModels for folder tree, and for Portal Header */
function processFolderActions(
  folderId: string,
  actionHeaders: ActionHeader[],
  handleAction: (_action?: BaseActionType | ActionHeader) => void,
  skipHeaderActions = false
) {
  // include navigation actions for folder tree:
  const headersForTree = filterActions(actionHeaders, {
    entityId: folderId,
    onlySingleAllowActions: true,
    isMobile: getIsMobile(),
  });

  // per actionsbarmodel.ts#createModel filtering
  // do not include navigation actions for portal/page header. Hopefully iterating a smaller set, here.
  const headersForHeader = skipHeaderActions
    ? []
    : filterActions(headersForTree, {
        entityId: folderId,
        ignoreNavigation: true,
        onlySingleAllowActions: true,
      });

  // These action links are technically derived state, but it's worth caching them here.
  // so this _could_ happen in background and then they're ready when user clicks
  return {
    folderTreeActions: getActionLinksForActions(filterFolderActions(headersForTree, folderId), handleAction),
    headerActions: getActionLinksForActions(filterFolderActions(headersForHeader, folderId), handleAction),
  };
}

export function subscribeToFolderViewData(folderId: string, callback: (nextFvd?: FolderViewData) => void) {
  return useFolderStore.subscribe((next, prev) => {
    const nextFvd = getFolderState(next, folderId).folderViewData;
    const prevFvd = getFolderState(prev, folderId).folderViewData;
    if (nextFvd !== prevFvd) {
      callback(nextFvd);
    }
  });
}
